/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useCallback, useEffect, useMemo, useReducer, useState } from "react";
import { useTranslation } from "react-i18next";
import U2000SaveCancelDialog from "../../../u2000Core/components/dialog/U2000SaveCancelDialog";
import U2000AutoCompleteInput from "../../../u2000Core/components/formControls/autoComplete/U2000AutoCompleteInput";
import U2000ComboBoxBase from "../../../u2000Core/components/formControls/combobox/U2000ComboBoxBase";
import U2000Label from "../../../u2000Core/components/formControls/label/U2000Label";
import u2000FormState, { U2000FormStateEnumEditMode, type U2000FormState } from "../../../u2000Core/reducer/formReducer/U2000FormState";
import { u2000IntFormState, useU2000FormStateHandlers } from "../../../u2000Core/reducer/formReducer/U2000FormStateHandlers";
import { U3129ProjectSearchPanel } from "../../../u3129Timesheet/components/form/U3129LineProjectSubprojectRowCells";
import type { U3129FindProjectItem } from "../../../u3129Timesheet/U3129Service";
import { NS, U3125ROLES } from "../../U3125";
import { useU2000AuthContext } from "../../../u2000Core/context/auth/U2000UseAuthContext";
import type { U3125InitCreateDocumentNature } from "../../U3125Service";
import { useU3125FindProjectHandlers } from "../../U3125UseFindProjectHandlers";

type DocumentNatureOption = { code: U3125InitCreateDocumentNature; description: string };

type U3125AddDlgFormRow = {
  project: { id?: string; projetNumber?: string; projetName?: string } | null;
};

const emptyAddDlgForm = (): U3125AddDlgFormRow => ({ project: null });

export interface U3125FactureAddDlgProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (projetNumber: string, documentNature: U3125InitCreateDocumentNature) => void | Promise<void>;
}

const U3125FactureAddDlg: React.FC<U3125FactureAddDlgProps> = ({ isOpen, onClose, onConfirm }) => {
  const { t } = useTranslation();
  const { hasRole } = useU2000AuthContext();
  const canViewTecLines = hasRole(U3125ROLES.VIEWTECLINES);
  const [documentNature, setDocumentNature] = useState<U3125InitCreateDocumentNature>("STANDARD");
  const [submitting, setSubmitting] = useState(false);
  const [formState, formStateDispatch] = useReducer<U2000FormState, any>(u2000FormState, u2000IntFormState);
  const findProjectHandlers = useU3125FindProjectHandlers();

  const formStateHandlers = useU2000FormStateHandlers<U3125AddDlgFormRow>({
    formState,
    formStateDispatch,
    businessHandlers: {
      newRecord: () => emptyAddDlgForm(),
      validateForm: (row) => {
        const result: Record<string, string> = {};
        if (!row.project?.projetNumber?.trim()) {
          result.project = t("errProjectRequired", { ns: NS });
        }
        return result;
      },
      saveForme: async () => {},
    },
  });

  const natureOptions: DocumentNatureOption[] = useMemo(() => {
    const options: DocumentNatureOption[] = [
      { code: "STANDARD", description: t("lblDocumentNatureStandard", { ns: NS }) },
      { code: "CREDIT", description: t("lblDocumentNatureCredit", { ns: NS }) },
      { code: "ADVANCE", description: t("lblDocumentNatureAdvance", { ns: NS }) },
    ];
    if (canViewTecLines) {
      options.push({ code: "WIP_WRITE_OFF", description: t("lblDocumentNatureWriteOff", { ns: NS }) });
    }
    return options;
  }, [t, canViewTecLines]);

  const selectedNature = useMemo(
    () => natureOptions.find((o) => o.code === documentNature) ?? natureOptions[0],
    [documentNature, natureOptions],
  );

  const selectedProject = formStateHandlers.getData()?.project;
  const projetNumber = selectedProject?.projetNumber?.trim() ?? "";

  useEffect(() => {
    if (!isOpen) return;
    setDocumentNature("STANDARD");
    setSubmitting(false);
    formStateHandlers.setEditMode(U2000FormStateEnumEditMode.ADD);
    formStateHandlers.setData(emptyAddDlgForm());
    // eslint-disable-next-line react-hooks/exhaustive-deps -- réinitialisation à l’ouverture uniquement
  }, [isOpen]);

  const onSearchProject = useCallback(
    async (criterias: { fieldName: string; value: unknown }[]) => findProjectHandlers.search(criterias),
    [findProjectHandlers],
  );

  const extractProject = useCallback((result: U3129FindProjectItem | null): { fieldName: string; value: unknown }[] => {
    if (result) {
      return [
        {
          fieldName: "project",
          value: { id: result.id, projetNumber: result.projetNumber, projetName: result.projetName },
        },
      ];
    }
    return [{ fieldName: "project", value: null }];
  }, []);

  if (!isOpen) return null;

  const handleSave = async () => {
    const num = projetNumber;
    if (!num || submitting) return;
    setSubmitting(true);
    try {
      await onConfirm(num, documentNature);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDialogClose = (action: "save" | "cancel") => {
    if (action === "cancel") {
      onClose();
      return;
    }
    void handleSave();
  };

  return (
    <U2000SaveCancelDialog
      isOpen={isOpen}
      onClose={handleDialogClose}
      title={t("dlgAddInvoiceTitle", { ns: NS })}
      isValid={Boolean(projetNumber) && !submitting}
      width="max-w-lg"
    >
      <div className="flex flex-col gap-4 p-1">
        <U2000AutoCompleteInput
          pageSize={15}
          formStateHandlers={formStateHandlers as any}
          fieldName="project"
          mainFieldName="projetNumber"
          entityLink="U3121"
          label={t("lblProject", { ns: NS })}
          onSearch={onSearchProject}
          displayFn={(item) => {
            const p = item as U3129FindProjectItem | null;
            return p ? (p.projetNumber ?? p.id ?? "") : "";
          }}
          overrideExtractDataFn={(item) => extractProject(item as U3129FindProjectItem | null)}
          basicResultHeight={300}
          resultLabels={[]}
          resultFields={["projetNumber", "projetName"]}
          minCarLaunchSearch={3}
          customSearchPanelHeight={500}
          panelWidth={400}
          dropdownWidth={400}
          renderPanel={(props) => <U3129ProjectSearchPanel {...props} ns="u3129" />}
          readonly={submitting}
          required
        />
        <div className="space-y-1.5">
          <U2000Label label={t("lblDocumentNature", { ns: NS })} />
          <U2000ComboBoxBase<DocumentNatureOption>
            data={natureOptions}
            keyField="code"
            descField="description"
            value={selectedNature}
            disabled={submitting}
            onChangeObject={(item) => setDocumentNature(item?.code ?? "STANDARD")}
          />
        </div>
      </div>
    </U2000SaveCancelDialog>
  );
};

export default U3125FactureAddDlg;
